import { useMemo, useEffect, useState } from "react";
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Popconfirm,
  Empty,
  Switch,
} from "antd";
import {
  Laptop,
  Plus,
  Settings,
  Clock,
  Trash2,
} from "lucide-react";
import MeetingTypeModal from "./MeetingTypeModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createMeetingType,
  deleteMeetingType,
  getMeetingTypes,
  updateMeetingType,
} from "../../actionCreator/meetingTypesActionCreator";
import { getAvailabilitySlots } from "../../actionCreator/availabilityActionCreator";
import { useAlert } from "../../context/AlertProvider";
import { MeetingType } from "../../config";
import { getUserInfo } from "../../utils/utils";


const { Text } = Typography;

const MeetingTypes = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { id: userId } = getUserInfo();
  const loading = useSelector((state) => state.meetingTypeReducer.loading);
  const meetingTypes = useSelector(
    (state) => state.meetingTypeReducer.meetingTypes,
  );
  const availabilitySlots = useSelector(
    (state) => state.availabilityReducer.availabilitySlots,
  );
  const [error, setError] = useState();

  const hasAvailability = useMemo(() => {
    if (!Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return false;
    }
    return availabilitySlots.some(
      (day) =>
        (Array.isArray(day?.timeSlots) && day.timeSlots.length > 0) ||
        (day?.startTime && day?.endTime),
    );
  }, [availabilitySlots]);

  useEffect(() => {
    dispatch(getMeetingTypes());
    dispatch(getAvailabilitySlots());
  }, [dispatch]);

  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      // case MeetingType.ZOOM:
      //   return <VideoCameraOutlined />;

      case MeetingType.GOOGLE_MEET:
        return <Laptop size={16} />;

      // case MeetingType.PHONE:
      //   return <PhoneOutlined />;

      // case MeetingType.IN_PERSON:
      //   return <UserOutlined />;

      default:
        return null;
    }
  };

  const handleAdd = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (data) => {
    setEditData(data);
    setOpen(true);
  };

  const handleSubmit = async (values, callback) => {
    setError();
    let res;
    if (editData) {
      res = await dispatch(updateMeetingType({ ...values, userId }));
    } else {
      res = await dispatch(createMeetingType({ ...values, userId }));
    }
    if (res?.status === 200) {
      showAlert(
        "success",
        editData
          ? `${values?.meetingName} Meeting type updated successfully.`
          : `${values?.meetingName} Meeting type created successfully.`,
      );
      dispatch(getMeetingTypes());
      callback();
      setOpen(false);
    } else {
      setError(res?.response?.data?.title);
      showAlert("error", res?.response?.data?.title);
    }
  };

  const handleDelete = async (id) => {
    if ((meetingTypes?.length || 0) <= 1) {
      showAlert("warning", "At least one meeting type should be there.");
      return;
    }

    const res = await dispatch(deleteMeetingType(id));
    if (res?.status === 204) {
      showAlert("success", "Meeting type deleted successfully.");
      dispatch(getMeetingTypes());
    } else {
      showAlert("error", res?.response?.data?.title);
    }
  };

  const handleToggleActive = async (item, checked) => {
    if (checked && !hasAvailability) {
      showAlert(
        "warning",
        "Availability must be added to your account before activating meeting types.",
      );
      return;
    }

    const res = await dispatch(
      updateMeetingType({ ...item, userId, isActive: checked }),
    );
    if (res?.status === 200) {
      showAlert(
        "success",
        `${item.meetingName} is now ${checked ? "Active" : "Inactive"}.`,
      );
      dispatch(getMeetingTypes());
    } else {
      showAlert("error", "Failed to update meeting type status.");
    }
  };

  return (
    <div className="tw-mt-6">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
        <div>
          <h2 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-1">Meeting Types</h2>
          <p className="tw-text-sm tw-text-slate-500">
            Manage different types of meetings you offer.
          </p>
        </div>
        <Button
          type="primary"
          className="theme-btn tw-h-[40px] !tw-rounded-xl tw-font-bold tw-text-xs tw-shadow-[0_8px_20px_rgba(93,68,255,0.25)] hover:tw-shadow-[0_12px_24px_rgba(93,68,255,0.35)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300"
          icon={<Plus size={16} />}
          onClick={handleAdd}
        >
          Add Meeting Type
        </Button>
      </div>

      <MeetingTypeModal
        visible={open}
        onCancel={() => {
          setOpen(false);
          setError();
        }}
        onSubmit={handleSubmit}
        loading={loading}
        initialValues={editData}
        error={error}
      />

      {/* Cards */}
      {meetingTypes?.length ? (
        <Row gutter={[24, 24]}>
          {meetingTypes.map((item) => (
            <Col xs={24} md={12} key={item.meetingTypeId}>
              <Card
                className="!tw-rounded-2xl !tw-border-slate-100/80 tw-shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:tw-shadow-[0_12px_30px_rgba(93,68,255,0.06)] hover:tw-scale-[1.005] tw-transition-all tw-duration-300 tw-overflow-hidden"
                title={
                  <div className="tw-py-2">
                    <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-1 tw-whitespace-normal">
                      {item.meetingName}
                    </h3>
                    <p className="tw-text-xs tw-text-slate-400 tw-font-normal tw-mb-0 tw-whitespace-normal tw-leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                }
                extra={
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <Switch
                      checked={item.isActive}
                      onChange={(checked) => handleToggleActive(item, checked)}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                    <Settings
                      size={18}
                      className="tw-text-slate-400 hover:tw-text-theme tw-cursor-pointer tw-text-base tw-transition-colors"
                      onClick={() => handleEdit(item)}
                    />
                    <Popconfirm
                      title={`Are you sure you want to delete ${item?.meetingName} meeting type?`}
                      onConfirm={() => handleDelete(item.meetingTypeId)}
                      disabled={(meetingTypes?.length || 0) <= 1}
                      okText="Yes"
                      cancelText="No"
                      okButtonProps={{
                        className: "theme-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                      }}
                      cancelButtonProps={{
                        className: "secondary-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                      }}
                    >
                      <Trash2
                        size={16}
                        className="tw-text-slate-400 hover:tw-text-red-500 tw-cursor-pointer tw-transition-colors"
                        onClick={() => {
                          if ((meetingTypes?.length || 0) <= 1) {
                            showAlert(
                              "warning",
                              "At least one meeting type should be there.",
                            );
                          }
                        }}
                      />
                    </Popconfirm>
                  </div>
                }
              >
                <Space direction="vertical" size={12} className="tw-w-full">
                  <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-xs tw-font-semibold tw-text-slate-600">
                    <Clock size={14} className="tw-text-slate-400" />
                    <span>{item.durationMinutes} minutes</span>
                  </div>
                  
                  {item.meetingPlatform && (
                    <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-xs tw-font-semibold tw-text-slate-600">
                      <span className="tw-text-slate-400 tw-text-sm">
                        {getMeetingTypeIcon(item.meetingPlatform)}
                      </span>
                      <span>
                        {item.meetingPlatform
                          ?.toLowerCase()
                          ?.replace(/_/g, " ")
                          ?.replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </div>
                  )}

                  <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-bg-slate-50 tw-inline-block tw-px-2.5 tw-py-1 tw-rounded-lg tw-border tw-border-solid tw-border-slate-100/50">
                    Max {item.maxPerDay} per day
                  </div>

                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mt-2">
                    {item.availableDays.map((day) => (
                      <Tag key={day} className="!tw-bg-indigo-50/50 !tw-border-indigo-100/30 !tw-text-theme !tw-text-[10px] !tw-font-bold !tw-rounded-md !tw-px-2 !tw-py-0.5 tw-uppercase">
                        {day}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No result found"
        />
      )}
    </div>
  );
};

export default MeetingTypes;
